import { NgClass } from '@angular/common';
import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
    ChangeDetectionStrategy,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { Subject, takeUntil, tap } from 'rxjs';
import {
    CardTypeEnum,
    CardTypeLabelMapping,
} from '../../../../../../core/enums/commerce/cards-on-file/card-types';
import { formatNumericalMonthToMM } from '../../../../../../core/functions/date-helpers';
import { hasOpenDialogs } from '../../../../../../core/functions/dialog-helper';
import { AuthenticatedUser } from '../../../../../../core/models/authenticated-user';
import { CreditCardSummary } from '../../../../../../core/models/commerce/cards-on-file/credit-card-summary';
import { CreditCardDialogService } from '../../../../../../core/services/commerce/credit-card-dialog/credit-card-dialog.service';
import { CreditCardSummaryService } from '../../../../../../core/services/commerce/credit-card-summary/credit-card-summary.service';
import { CardTypeIconComponent } from '../../../../../../shared/components/card-type-icon/card-type-icon.component';
import { UserStateService } from '../../../../shared/services/user-state/user-state.service';

@Component({
    selector: 'app-card-on-file',
    templateUrl: './card-on-file.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [NgClass, MatButtonModule, CardTypeIconComponent],
})
export class CardOnFileComponent implements OnDestroy {
    private readonly destroy$ = new Subject<void>();

    cardTypeLabelMapping = CardTypeLabelMapping;

    @Input() card!: CreditCardSummary;
    @Input() cardsOnFile!: CreditCardSummary[];
    @Output() refreshCardsOnFile = new EventEmitter<void>();

    authenticatedUser: AuthenticatedUser | null =
        this.userStateService.getUser();

    constructor(
        private readonly dialog: MatDialog,
        private readonly userStateService: UserStateService,
        private readonly creditCardDialogService: CreditCardDialogService,
        private readonly creditCardSummaryService: CreditCardSummaryService,
    ) {}

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    isExpired(): boolean {
        return this.creditCardSummaryService.isExpired(this.card);
    }

    isVisa(): boolean {
        return this.card.cardType === CardTypeEnum.Visa;
    }

    isMasterCard(): boolean {
        return this.card.cardType === CardTypeEnum.Mastercard;
    }

    isAmex(): boolean {
        return this.card.cardType === CardTypeEnum.Amex;
    }

    formatExpirationMonthForDisplay(): string {
        return formatNumericalMonthToMM(this.card.expirationMonth);
    }

    handleEditCreditCardClick(): void {
        if (!this.authenticatedUser) {
            return;
        }

        if (hasOpenDialogs(this.dialog)) {
            return;
        }

        this.creditCardDialogService
            .openEditCardDialog(
                this.card.id,
                this.authenticatedUser.id,
                this.cardsOnFile,
            )
            .pipe(
                takeUntil(this.destroy$),
                tap((result: boolean) => {
                    if (result) {
                        this.refreshCardsOnFile.emit();
                    }
                }),
            )
            .subscribe();
    }

    handleDeleteCreditCardClick(): void {
        if (hasOpenDialogs(this.dialog)) {
            return;
        }

        this.creditCardDialogService
            .openDeleteCardDialog(this.card.id)
            .pipe(
                takeUntil(this.destroy$),
                tap((result: boolean) => {
                    if (result) {
                        this.refreshCardsOnFile.emit();
                    }
                }),
            )
            .subscribe();
    }
}
